import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';

let stripePromiseCache = null;

/**
 * Extract a clean, user-friendly error message from a Firebase callable error.
 */
export function getPaymentErrorMessage(error) {
  // Firebase HttpsError puts the custom message in error.message
  // but sometimes prepends the error code like "internal: ..."
  const msg = error?.message || 'An unexpected error occurred.';

  // Strip Firebase error code prefix if present (e.g. "internal: Your card...")
  const prefixMatch = msg.match(/^(internal|failed-precondition|not-found|permission-denied|invalid-argument|unauthenticated):\s*/i);
  if (prefixMatch) {
    return msg.slice(prefixMatch[0].length);
  }

  return msg;
}

/**
 * Hook that fetches the Stripe publishable key from Cloud Functions
 * and returns a loadStripe promise.
 */
export const useStripeConfig = () => {
  const [stripePromise, setStripePromise] = useState(stripePromiseCache);
  const [loading, setLoading] = useState(!stripePromiseCache);

  useEffect(() => {
    if (stripePromiseCache) return;

    const fetchConfig = async () => {
      try {
        const functions = getFunctions();
        const getConfig = httpsCallable(functions, 'getStripeConfig');
        const result = await getConfig();
        const key = result.data.publishableKey;
        if (key) {
          stripePromiseCache = loadStripe(key);
          setStripePromise(stripePromiseCache);
        }
      } catch (err) {
        console.error('Failed to fetch Stripe config:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { stripePromise, loading };
};

/**
 * Call the createPaymentIntent Cloud Function
 */
export const callCreatePaymentIntent = async (paymentMethodId, bountyAmount) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'createPaymentIntent');
  const result = await fn({ paymentMethodId, bountyAmount });
  return result.data;
};

/**
 * Call the processClaimPayment Cloud Function
 */
export const callProcessClaimPayment = async (claimId, bountyId, rating, feedback) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'processClaimPayment');
  const result = await fn({ claimId, bountyId, rating, feedback });
  return result.data;
};

/**
 * Call the cancelPaymentHold Cloud Function
 */
export const callCancelPaymentHold = async (bountyId) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'cancelPaymentHold');
  const result = await fn({ bountyId });
  return result.data;
};

/**
 * Call the updateFeePercent Cloud Function (admin)
 */
export const callUpdateFeePercent = async (feePercent) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'updateFeePercent');
  const result = await fn({ feePercent });
  return result.data;
};

/**
 * Call the addPaymentMethod Cloud Function
 */
export const callAddPaymentMethod = async (paymentMethodId) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'addPaymentMethod');
  const result = await fn({ paymentMethodId });
  return result.data;
};

/**
 * Call the removePaymentMethod Cloud Function
 */
export const callRemovePaymentMethod = async (paymentMethodId) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'removePaymentMethod');
  const result = await fn({ paymentMethodId });
  return result.data;
};

/**
 * Call the adminResolvePayment Cloud Function (admin)
 */
export const callAdminResolvePayment = async (issueId) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'adminResolvePayment');
  const result = await fn({ issueId });
  return result.data;
};

// ========================================================================
// Stripe Connect — Researcher payouts
// ========================================================================

export const callCreateStripeConnectAccount = async () => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'createStripeConnectAccount');
  const result = await fn({});
  return result.data;
};

export const callCreateStripeDashboardLink = async () => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'createStripeDashboardLink');
  const result = await fn({});
  return result.data;
};

export const callGetStripeConnectStatus = async () => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'getStripeConnectStatus');
  const result = await fn({});
  return result.data;
};

export const callGetAvailableBalance = async () => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'getAvailableBalance');
  const result = await fn({});
  return result.data;
};

export const callRequestPayout = async (amount) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'requestPayout');
  const result = await fn({ amount });
  return result.data;
};

export const callAdminApprovePayout = async (requestId) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'adminApprovePayout');
  const result = await fn({ requestId });
  return result.data;
};

export const callAdminRejectPayout = async (requestId, reason) => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'adminRejectPayout');
  const result = await fn({ requestId, reason });
  return result.data;
};

// ========================================================================
// Account deletion
// ========================================================================

export const callDeleteAccount = async () => {
  const functions = getFunctions();
  const fn = httpsCallable(functions, 'deleteAccount');
  const result = await fn({});
  return result.data;
};
